"use client";
import { cn } from "@/lib/utils";
import { useTranslations } from "next-intl";

// ============================================================================
// Pain — Comparison Table (Without Coldfire vs With Coldfire)
// ============================================================================

export const PainComparisonTable = () => {
  const t = useTranslations("painB");

  const rows = [1, 2, 3, 4, 5, 6, 7, 8].map((i) => ({
    label: t(`row_${i}_label`),
    without: t(`row_${i}_without`),
    with: t(`row_${i}_with`),
  }));

  return (
    <section className="relative w-full py-20 md:py-28">
      <div className="mx-auto max-w-5xl px-4 md:px-6 lg:px-8">
        <div className="text-center">
          <h2 className="text-3xl md:text-5xl font-bold leading-tight tracking-tight text-white">
            {t("heading")}
          </h2>
        </div>

        {/* Desktop: 3-Spalten-Tabelle */}
        <div className="mt-12 hidden md:block overflow-hidden rounded-2xl border border-neutral-800 bg-neutral-950">
          <div className="grid grid-cols-3 border-b border-neutral-800">
            <div className="p-4 md:p-5 text-[10px] uppercase tracking-wider font-semibold text-neutral-500" />
            <div className="p-4 md:p-5 border-l border-neutral-800">
              <div className="text-sm font-semibold text-neutral-500">
                {t("col_without")}
              </div>
            </div>
            <div className="p-4 md:p-5 border-l border-neutral-800 bg-neutral-900/60">
              <div className="text-sm font-semibold text-white">
                {t("col_with")}
              </div>
            </div>
          </div>

          {rows.map((row, i) => (
            <div
              key={i}
              className={cn(
                "grid grid-cols-3 text-sm md:text-base",
                i < rows.length - 1 && "border-b border-neutral-800/60"
              )}
            >
              <div className="p-4 md:p-5 text-neutral-300 font-medium">
                {row.label}
              </div>
              <div className="p-4 md:p-5 border-l border-neutral-800/60 text-neutral-500">
                {row.without}
              </div>
              <div className="p-4 md:p-5 border-l border-neutral-800/60 bg-neutral-900/60">
                <div className="flex items-start gap-2">
                  <CheckIcon className="mt-0.5 h-4 w-4 text-red-500 flex-shrink-0" />
                  <span className="text-neutral-200">{row.with}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Mobile: kompakte Karten-Liste mit ✗/✓-Markern */}
        <div className="mt-10 md:hidden space-y-3">
          {rows.map((row, i) => (
            <div
              key={i}
              className="rounded-xl border border-neutral-800 bg-neutral-950 p-4"
            >
              <div className="text-sm font-semibold text-white">
                {row.label}
              </div>
              <div className="mt-3 space-y-2 text-sm">
                <div className="flex items-start gap-2.5">
                  <XIcon className="mt-0.5 h-4 w-4 text-neutral-600 flex-shrink-0" />
                  <span className="text-neutral-500">
                    {row.without}
                  </span>
                </div>
                <div className="flex items-start gap-2.5">
                  <CheckIcon className="mt-0.5 h-4 w-4 text-red-500 flex-shrink-0" />
                  <span className="text-neutral-200">{row.with}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============================================================================
// Icons
// ============================================================================

const CheckIcon = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2.2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden
  >
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

const XIcon = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2.2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden
  >
    <path d="M6 6 18 18 M18 6 6 18" />
  </svg>
);
